/**
 * Dashboard sign-in -- a typed email+password form (ports shop/ui's
 * AdminLoginPage.tsx form conventions: filled fields, single primary-blue
 * submit, an inline alert for errors). No PIN pad, no float -- this is a
 * back-office login, not a shift clock-in. Neutral, quiet card on the
 * scheme background -- no top-edge accent strip (owner ruling 2026-07-14:
 * "tell-tale AI implementation").
 */
import { useState } from 'react';
import { Navigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { ApiError } from '../api/index.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { APP_TITLE, APP_VERSION, TURNSTILE_SITE_KEY } from '../config.ts';
import { palette } from '../theme.ts';
import { firstAllowedRoute } from '../routing/routes.ts';
import { TurnstileWidget } from './TurnstileWidget.tsx';

/** Primary blue, the same var FOCUS_RING/theme.ts use for interactive accents. */
const FOCUS_COLOR = 'var(--mui-palette-primary-main)';

const fieldSx = {
  '& .MuiFilledInput-root': {
    backgroundColor: 'action.hover',
    borderRadius: 1.5,
    overflow: 'hidden',
    '&:hover': { backgroundColor: 'action.selected' },
    '&.Mui-focused': { backgroundColor: 'action.selected' },
    '&:before': { borderBottomColor: palette.quietBorder },
    '&:hover:before': { borderBottomColor: palette.quietBorderHover },
    '&:after': { borderBottomColor: FOCUS_COLOR, borderBottomWidth: 3 },
  },
  '& .MuiFilledInput-input': { color: 'text.primary', fontSize: '1.1rem' },
  '& .MuiInputLabel-root': { color: palette.textSecondary },
  '& .MuiInputLabel-root.Mui-focused': { color: FOCUS_COLOR },
} as const;

function errorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.isRateLimited) return err.message;
    // The two Turnstile refusals (server routes/auth.ts): a rejected token,
    // and a siteverify outage the server fails closed on.
    if (err.code === 'turnstile_failed') return 'Verification failed - please retry.';
    if (err.code === 'turnstile_unavailable') return 'Verification is temporarily unavailable. Please try again.';
    if (err.status === 401) {
      // The server only ever sends this particular message AFTER a correct
      // password (E2-API-REFERENCE.md's auth failure list) -- a real,
      // actionable distinction from "wrong credentials" worth surfacing
      // verbatim rather than collapsing into the generic copy below.
      if (err.message.toLowerCase().includes('inactive')) return err.message;
      return 'Incorrect email or password.';
    }
    if (err.isNetworkError) return 'Cannot reach the server. Check the connection and try again.';
    return err.message;
  }
  return 'Sign-in failed. Please try again.';
}

export interface LoginPageProps {
  /** Test seam / build-time switch: empty string means no Turnstile gate. */
  siteKey?: string;
}

export function LoginPage({ siteKey = TURNSTILE_SITE_KEY }: Readonly<LoginPageProps> = {}): React.JSX.Element {
  const { login, isAuthenticated, state } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileReset, setTurnstileReset] = useState(0);
  const [turnstileLoadFailed, setTurnstileLoadFailed] = useState(false);
  // Remount key: the retry affordance rebuilds the widget from scratch, which
  // is what re-attempts the script load.
  const [turnstileMount, setTurnstileMount] = useState(0);

  const gated = siteKey !== '';

  function retryTurnstile(): void {
    setTurnstileLoadFailed(false);
    setTurnstileMount((n) => n + 1);
  }

  async function submit(e: React.SyntheticEvent): Promise<void> {
    e.preventDefault();
    if (busy) return;
    if (email.trim() === '' || password === '') {
      setError('Enter your email and password.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      // No explicit navigate() here: a successful login dispatches into
      // AuthContext, which re-renders this component with isAuthenticated
      // true and the resolved permissions already on `state` -- the guard
      // below then redirects. Reading `state.permissions` right after this
      // await would race a stale closure (this function's `state` is
      // whatever it was when submit() was created, not what login() just
      // set), so the redirect target is computed only on a fresh render.
      await login(email.trim(), password, turnstileToken ?? undefined);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
      // Turnstile tokens are single-use: a rejected sign-in leaves a spent
      // token behind, so force a fresh challenge before the next attempt.
      if (gated) {
        setTurnstileToken(null);
        setTurnstileReset((n) => n + 1);
      }
    }
  }

  if (isAuthenticated) {
    return <Navigate to={firstAllowedRoute(state.permissions)} replace />;
  }

  return (
    <Box
      sx={{
        height: '100vh',
        bgcolor: 'background.default',
        color: 'text.primary',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          bgcolor: 'background.paper',
          border: `1px solid ${palette.quietBorder}`,
          boxShadow: '0 24px 64px rgba(0,0,0,0.45)',
          borderRadius: 3,
          p: { xs: 3, sm: 4 },
          width: '100%',
          maxWidth: 420,
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            {APP_TITLE}
          </Typography>
          <Typography
            sx={{
              mt: 0.75,
              fontWeight: 700,
              fontSize: '0.72rem',
              letterSpacing: 3,
              textTransform: 'uppercase',
              color: palette.textSecondary,
            }}
          >
            Dashboard sign-in
          </Typography>
        </Box>

        <Box component="form" onSubmit={(e) => void submit(e)} sx={{ display: 'flex', flexDirection: 'column' }}>
          <TextField
            label="Email"
            type="email"
            variant="filled"
            fullWidth
            margin="dense"
            autoFocus
            autoComplete="email"
            sx={fieldSx}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            slotProps={{ htmlInput: { 'aria-label': 'Email' } }}
          />
          <TextField
            label="Password"
            type="password"
            variant="filled"
            fullWidth
            margin="dense"
            autoComplete="current-password"
            sx={fieldSx}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            slotProps={{ htmlInput: { 'aria-label': 'Password' } }}
          />

          {gated && (
            <TurnstileWidget
              key={turnstileMount}
              siteKey={siteKey}
              onToken={setTurnstileToken}
              resetSignal={turnstileReset}
              onLoadError={() => setTurnstileLoadFailed(true)}
            />
          )}
          {gated && turnstileLoadFailed && (
            <Alert
              role="alert"
              severity="warning"
              variant="filled"
              sx={{ fontWeight: 700 }}
              action={
                <Button size="small" color="inherit" onClick={retryTurnstile}>
                  Retry
                </Button>
              }
            >
              Verification could not load - check your connection and any content blockers.
            </Alert>
          )}

          <Box sx={{ minHeight: 52, mt: 1.5 }}>
            {busy && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} sx={{ color: FOCUS_COLOR }} />
                <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                  Signing in...
                </Typography>
              </Box>
            )}
            {!busy && error && (
              <Alert role="alert" severity="error" variant="filled" sx={{ fontWeight: 700 }}>
                {error}
              </Alert>
            )}
          </Box>

          <Button
            type="submit"
            disabled={busy || (gated && turnstileToken === null)}
            sx={{ mt: 1, minHeight: 48, fontSize: '1rem' }}
          >
            Sign in
          </Button>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2.5 }}>
          <Typography variant="caption" sx={{ color: palette.textSecondary }}>
            {APP_TITLE} v{APP_VERSION}
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}

export default LoginPage;
